import React, { useMemo, useState, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Tooltip } from '@mui/material';
import {
  HelpOutline, ExpandMore,
  CalendarMonthOutlined, Inventory2Outlined, CalculateOutlined, PriceCheckOutlined, ShowChartOutlined,
  SummarizeOutlined, TouchAppOutlined,
} from '@mui/icons-material';
import { useBaseHedgeMaterials, useDerivedMaterials } from '../../../hooks/useBaseHedgeMaterials';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import { getDerivedHedges, getHedgePositions, HedgePosition } from '../../../services/hedgesService';
import { getDemandPositions } from '../../../services/demandService';
import { getSupplyPositions } from '../../../services/supplyService';
import { calculateTerminalCoverage, TerminalData, TerminalCoverageData } from './terminalCoverageCalculator';
import { useFx } from '../../../hooks/useFx';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';

interface Props {
  accountId: number;
  baseMaterialId: string;
}

// Default view horizon (client request) — user-adjustable via the header selector.
const DEFAULT_HORIZON_MONTHS = 18;
const HORIZON_OPTIONS = [6, 9, 12, 18, 24, 36];

// Base hedge instrument (e.g. LCC) trades in 10 MT contracts.
const CONTRACT_SIZE_MT = 10;

const HEB_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const fmt = (n: number, decimals = 0) =>
  n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const n1 = (v: number) => fmt(v, 1);
/** Signed, one decimal (tooltips): +12.3 / −4.0 / 0 */
const signed1 = (v: number) => (Math.abs(v) < 0.05 ? '0' : `${v > 0 ? '+' : '−'}${fmt(Math.abs(v), 1)}`);

const CCY_SYMBOL: Record<string, string> = { USD: '$', GBP: '£', GBX: 'p', EUR: '€', ILS: '₪' };
const ccyPrefix = (c?: string): string => {
  if (!c) return '$';
  const u = c.toUpperCase();
  return CCY_SYMBOL[u] ?? `${u} `;
};

/** Short Hebrew material name (חמאה / עיסה) from the formula name, falling back to the name/code. */
const heName = (name?: string, code?: string) => {
  const s = (name || code || '').toLowerCase();
  if (s.includes('butter') || s.includes('bleu')) return 'חמאה';
  if (s.includes('mass') || s.includes('liquor') || s.includes('lleu')) return 'עיסה';
  return name || code || '';
};

// ─── Rows — read top to bottom like the calculation itself ────────────────────
// need:   consumption + emergency stock − existing stock  =  required contracts
// have:   exchange contracts + fixed-price POs            =  priced contracts
// result: gap, coverage · info: avg price, closed ratio
// `detail` rows are the intermediate steps; the compact view hides them (= the Strauss rows).

type Section = 'need' | 'have' | 'result' | 'info';
const SECTIONS: Record<Section, { label: string; hint: string; color: string }> = {
  need:   { label: 'מה צריך לגדר', hint: 'צריכה + מלאי חירום − מלאי קיים = חשיפה, ובפולים', color: '#059669' },
  have:   { label: 'מה כבר מתומחר', hint: 'חוזי בורסה + הזמנות במחיר קבוע', color: '#7c3aed' },
  result: { label: 'התוצאה', hint: 'מתומחר מול נדרש', color: '#d97706' },
  info:   { label: 'מידע נוסף', hint: '', color: '#64748b' },
};

const ROW_DEFS = [
  { key: 'monthly_demand', section: 'need',   sign: '',  detail: false, label: 'צריכה חודשית', subtitle: 'טון (חמאה + עיסה)' },
  { key: 'demand',         section: 'need',   sign: '+', detail: false, label: 'צריכה לטרמינל', subtitle: 'טון · סכום הצריכה החודשית' },
  { key: 'buffer',         section: 'need',   sign: '+', detail: true,  label: 'מלאי חירום', subtitle: 'טון · צריכת החודשיים שאחרי הטרמינל' },
  { key: 'available',      section: 'need',   sign: '−', detail: true,  label: 'מלאי קיים', subtitle: 'טון · נוכחי: מלאי פיזי · הבאים: מלאי החירום הקודם' },
  { key: 'required',       section: 'need',   sign: '=', detail: false, label: 'כמות חוזים נדרשת', subtitle: 'חוזים · חשיפה × רשיו ÷ 10' },
  { key: 'exchange',       section: 'have',   sign: '+', detail: true,  label: 'חוזי בורסה', subtitle: 'חוזים · פוקעים בטרמינל, כולל אופציות' },
  { key: 'pos',            section: 'have',   sign: '+', detail: true,  label: 'הזמנות רכש (PO)', subtitle: 'חוזים · מחיר קבוע, טון × רשיו ÷ 10' },
  { key: 'priced',         section: 'have',   sign: '=', detail: false, label: 'כמות חוזים מתומחרים', subtitle: 'חוזים' },
  { key: 'gap',            section: 'result', sign: '',  detail: false, label: 'כמות חוזים חסרה / עודפת', subtitle: 'מתומחרים − נדרשת · + עודף · − חוסר' },
  { key: 'coverage',       section: 'result', sign: '',  detail: false, label: 'אחוז כיסוי', subtitle: 'מתומחרים ÷ נדרשת' },
  { key: 'avg_price',      section: 'info',   sign: '',  detail: false, label: 'מחיר בורסה ממוצע', subtitle: 'אופציות לפי מחיר הסטרייק' },
  { key: 'closed_ratio',   section: 'info',   sign: '',  detail: false, label: 'רשיו סגור', subtitle: 'ממוצע הזמנות הרשיו בטרמינל' },
] as const;

type RowKey = typeof ROW_DEFS[number]['key'];

function cellValue(key: RowKey, t: TerminalData): number | null {
  switch (key) {
    case 'demand':       return t.demandMT;
    case 'buffer':       return t.bufferMT;
    case 'available':    return t.inventoryMT;
    case 'exchange':     return t.actualMT / CONTRACT_SIZE_MT;
    case 'pos':          return t.posBeansMT / CONTRACT_SIZE_MT;
    case 'required':     return t.requiredMT / CONTRACT_SIZE_MT;
    case 'priced':       return t.pricedMT / CONTRACT_SIZE_MT;
    case 'gap':          return t.gapMT / CONTRACT_SIZE_MT;
    case 'avg_price':    return t.avgPrice;
    case 'closed_ratio': return t.agreedRatio;
    case 'coverage':     return t.coveragePct;
    default:             return null;
  }
}

function formatCell(key: RowKey, v: number | null, currency?: string): string {
  if (v === null) return '—';
  switch (key) {
    case 'coverage':     return Number.isFinite(v) ? `${Math.round(v)}%` : '∞';
    // Contract counts are shown as whole contracts in the cells (client preference);
    // the tooltips keep one decimal so the arithmetic stays traceable.
    case 'gap':          return Math.round(v) === 0 ? '0' : `${v > 0 ? '+' : '−'}${fmt(Math.abs(Math.round(v)))}`;
    case 'required':
    case 'priced':       return fmt(Math.round(v));
    case 'exchange':
    case 'pos':          return Math.round(v) === 0 ? '—' : fmt(Math.round(v));
    case 'avg_price':    return v > 0 ? `${ccyPrefix(currency)}${fmt(v)}` : '—';
    case 'closed_ratio': return v > 0 ? v.toFixed(2) : '—';
    default:             return v > 0 ? fmt(v) : '—';
  }
}

function cellColor(key: RowKey, v: number | null): string {
  if (v === null) return '#374151';
  if (key === 'gap') return v < -0.05 ? '#dc2626' : v > 0.05 ? '#059669' : '#6b7280';
  if (key === 'coverage') return v >= 100 ? '#059669' : v >= 80 ? '#d97706' : '#dc2626';
  return '#1f2937';
}

function cellBg(key: RowKey, v: number | null): string | undefined {
  if (v === null) return undefined;
  if (key === 'coverage') return v >= 100 ? 'rgba(16,185,129,0.12)' : v >= 80 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)';
  if (key === 'gap' && Math.abs(v) >= 0.05) return v < 0 ? 'rgba(239,68,68,0.10)' : 'rgba(16,185,129,0.10)';
  if (key === 'priced' && v > 0) return 'rgba(59,130,246,0.07)';
  return undefined;
}

const isBold = (key: RowKey) => key === 'required' || key === 'priced' || key === 'gap' || key === 'coverage';

// ─── Formula building blocks (shared by the explanation panel and the cell tooltips) ───
// Every calculation is an isolated LEFT-TO-RIGHT row: a value box per term with its Hebrew label
// underneath, so numbers, operators and Hebrew words never get reordered by the bidi algorithm.

type Role = 'plus' | 'minus' | 'factor' | 'divisor' | 'result' | 'neutral';
const ROLE: Record<Role, { bg: string; fg: string; bd: string; tint: string }> = {
  plus:    { bg: '#ecfdf5', fg: '#047857', bd: '#a7f3d0', tint: '#dcfce7' },
  minus:   { bg: '#fef2f2', fg: '#b91c1c', bd: '#fecaca', tint: '#fee2e2' },
  factor:  { bg: '#eff6ff', fg: '#1d4ed8', bd: '#bfdbfe', tint: '#dbeafe' },
  divisor: { bg: '#f5f3ff', fg: '#6d28d9', bd: '#ddd6fe', tint: '#ede9fe' },
  result:  { bg: '#fffbeb', fg: '#92400e', bd: '#fcd34d', tint: '#fde68a' },
  neutral: { bg: '#f8fafc', fg: '#334155', bd: '#e2e8f0', tint: '#f1f5f9' },
};

const FONT = "'Heebo', 'Open Sans', system-ui, sans-serif";

/** Small rounded tag (used sparingly: header badge, legend). */
const Badge: React.FC<{ role?: Role; children: React.ReactNode }> = ({ role = 'neutral', children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', background: ROLE[role].bg, color: ROLE[role].fg,
    border: `1px solid ${ROLE[role].bd}`, borderRadius: 999, padding: '0 9px', fontWeight: 600, fontSize: 11.5,
    whiteSpace: 'nowrap', lineHeight: '20px',
  }}>{children}</span>
);

/** One term of a formula: the value in a box, its meaning (Hebrew) underneath. */
const Term: React.FC<{ v: React.ReactNode; label?: React.ReactNode; role?: Role }> = ({ v, label, role = 'neutral' }) => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
    <span dir="auto" style={{
      background: ROLE[role].bg, color: ROLE[role].fg, border: `1px solid ${ROLE[role].bd}`, borderRadius: 7,
      padding: '0 9px', fontWeight: 700, fontSize: 13, lineHeight: '22px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
    }}>{v}</span>
    {label && <span dir="rtl" style={{ fontSize: 10.5, color: '#64748b', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{label}</span>}
  </span>
);
const Op: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: 15, lineHeight: '24px', padding: '0 1px' }}>{children}</span>
);
/** A left-to-right formula row. */
const Formula: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div dir="ltr" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'flex-end', gap: '8px 6px', unicodeBidi: 'isolate' }}>{children}</div>
);
/** Numbers / dates inside Hebrew sentences. */
const Num: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <bdi dir="ltr" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{children}</bdi>
);
const fmtDate = (iso: string) => { const [y, m, d] = iso.slice(0, 10).split('-'); return `${d}.${m}.${y.slice(2)}`; };

// ─── "How is it calculated" — the explanation panel ───────────────────────────

const FormulaBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ background: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: 10, padding: '10px 10px 8px', margin: '8px 0' }}>{children}</div>
);
const Rule: React.FC<{ ok?: boolean; children: React.ReactNode }> = ({ ok, children }) => (
  <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', fontSize: 12.5, lineHeight: 1.6 }}>
    <span style={{ flexShrink: 0, width: 14, textAlign: 'center', fontWeight: 800, color: ok === undefined ? '#94a3b8' : ok ? '#059669' : '#dc2626' }}>
      {ok === undefined ? '•' : ok ? '✓' : '✕'}
    </span>
    <span>{children}</span>
  </div>
);

const Step: React.FC<{ n: number; icon: React.ReactNode; color: string; title: string; lead?: React.ReactNode; children: React.ReactNode }> = ({ n, icon, color, title, lead, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 9, background: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color }}>שלב {n}</span>
      <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{title}</span>
    </div>
    {lead && <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>{lead}</div>}
    {children}
  </div>
);

const HowItWorks: React.FC<{ lead: number; open: boolean; onToggle: () => void }> = ({ lead, open, onToggle }) => (
  <div className="mx-4 mt-3 rounded-xl" style={{ background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)', border: '1px solid #e2e8f0', fontFamily: FONT }}>
    <button
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center justify-between px-4 py-2.5"
      style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'right', fontFamily: FONT }}
    >
      <span className="flex items-center gap-2">
        <HelpOutline style={{ fontSize: 18, color: '#2563eb' }} />
        <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>איך הטבלה מחושבת?</span>
        <Badge>6 שלבים</Badge>
      </span>
      <span className="flex items-center gap-1" style={{ fontSize: 12, color: '#64748b' }}>
        {open ? 'הסתר' : 'הצג'}
        <ExpandMore style={{ fontSize: 20, transition: 'transform .2s ease', transform: open ? 'rotate(180deg)' : 'none' }} />
      </span>
    </button>
    {/* Smooth height animation: grid rows 0fr ↔ 1fr */}
    <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows .25s ease' }}>
      <div style={{ overflow: 'hidden' }}>
        <div className="px-4 pb-4">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            <Step n={1} icon={<CalendarMonthOutlined style={{ fontSize: 18 }} />} color="#0284c7" title="טרמינלים"
              lead={lead > 0 ? <>כל טרמינל אחראי על הצריכה של <b>{lead} החודשים שאחרי</b> חודש החוזה שלו.</> : <>כל טרמינל אחראי על חודשי הצריכה עד חודש החוזה שלו.</>}>
              {lead > 0 && (
                <FormulaBox>
                  <Formula><Term v="דצמבר" label="חודש החוזה" /><Op>→</Op><Term v="דצמבר–פברואר" label="חודשי הצריכה" role="plus" /></Formula>
                </FormulaBox>
              )}
              <Rule>העמודה הראשונה, <span style={{ background: '#fef9c3', padding: '0 4px', borderRadius: 4 }}>בצהוב</span>, היא הטרמינל של החודש הנוכחי, גם אם החוזה שלו כבר פקע.</Rule>
            </Step>

            <Step n={2} icon={<Inventory2Outlined style={{ fontSize: 18 }} />} color="#059669" title="חשיפה"
              lead={<>כמה טון עוד צריך לגדר. מחושב <b>לכל חומר בנפרד</b> (חמאה, עיסה).</>}>
              <FormulaBox>
                <Formula>
                  <Term v="צריכה" label="בחודשי הטרמינל" role="plus" /><Op>+</Op>
                  <Term v="מלאי חירום" label="צריכת החודשיים הבאים" role="plus" /><Op>−</Op>
                  <Term v="מלאי קיים" role="minus" /><Op>=</Op>
                  <Term v="חשיפה" label="טון" role="result" />
                </Formula>
              </FormulaBox>
              <Rule><b>מלאי קיים, טרמינל נוכחי:</b> המלאי הפיזי האחרון שדווח, כפי שהוא. תאריך הדיווח מופיע בהסבר של התא.</Rule>
              <Rule><b>מלאי קיים, טרמינלים הבאים:</b> מלאי החירום של הטרמינל הקודם. מניחים שהקודם גודר במלואו.</Rule>
              <Rule>החשיפה לא יורדת מתחת ל-<Num>0</Num>.</Rule>
            </Step>

            <Step n={3} icon={<CalculateOutlined style={{ fontSize: 18 }} />} color="#2563eb" title="כמות חוזים נדרשת"
              lead={<>ממירים כל חומר לטון פולים לפי הרשיו שלו, ומחלקים בגודל חוזה. חמאה ועיסה מתחברות <b>רק כאן</b>.</>}>
              <FormulaBox>
                <Formula>
                  <Op>(</Op><Term v="חשיפה" label="חמאה" role="plus" /><Op>×</Op><Term v="רשיו" label="חמאה" role="factor" />
                  <Op>+</Op><Term v="חשיפה" label="עיסה" role="plus" /><Op>×</Op><Term v="רשיו" label="עיסה" role="factor" /><Op>)</Op>
                  <Op>÷</Op><Term v="10" label="טון לחוזה" role="divisor" /><Op>=</Op><Term v="נדרשים" label="חוזים" role="result" />
                </Formula>
              </FormulaBox>
              <Rule><b>הרשיו:</b> לכל חומר נקבע רשיו יומי (LSEG) או רשיו קבוע. ברשיו יומי נלקח הערך האחרון שפורסם, והתאריך שלו מופיע בהסבר של התא.</Rule>
              <Rule>שינוי הרשיו: מדיניות <b>Hedges Deriving</b> ← Derived Materials ← אייקון העריכה.</Rule>
            </Step>

            <Step n={4} icon={<PriceCheckOutlined style={{ fontSize: 18 }} />} color="#7c3aed" title="כמות חוזים מתומחרים"
              lead={<>מה שכבר מגודר במחיר ידוע.</>}>
              <FormulaBox>
                <Formula>
                  <Term v="חוזי בורסה" label="פוקעים בטרמינל" role="plus" /><Op>+</Op>
                  <Term v="הזמנות רכש" label="מחיר קבוע, בפולים" role="plus" /><Op>=</Op>
                  <Term v="מתומחרים" label="חוזים" role="result" />
                </Formula>
              </FormulaBox>
              <Rule ok>אופציה נספרת כחוזה, ובמחיר הממוצע לפי מחיר הסטרייק.</Rule>
              <Rule ok={false}>הזמנת רשיו לא נספרת: המחיר שלה עוד לא נקבע.</Rule>
              <Rule ok={false}>הזמנה שמועד האספקה שלה עבר לא נספרת: היא כבר במלאי.</Rule>
            </Step>

            <Step n={5} icon={<ShowChartOutlined style={{ fontSize: 18 }} />} color="#d97706" title="חסר / עודף וכיסוי"
              lead={<>משווים את המתומחרים לנדרשים.</>}>
              <FormulaBox>
                <div style={{ display: 'grid', gap: 10 }}>
                  <Formula><Term v="מתומחרים" role="plus" /><Op>−</Op><Term v="נדרשים" role="minus" /><Op>=</Op><Term v="חסר / עודף" label="+ עודף · − חוסר" role="result" /></Formula>
                  <Formula><Term v="מתומחרים" role="plus" /><Op>÷</Op><Term v="נדרשים" role="divisor" /><Op>=</Op><Term v="% כיסוי" role="result" /></Formula>
                </div>
              </FormulaBox>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 11.5 }}>
                <span style={{ background: 'rgba(16,185,129,0.14)', color: '#047857', borderRadius: 6, padding: '1px 8px', fontWeight: 600 }}><Num>100%+</Num> מכוסה</span>
                <span style={{ background: 'rgba(245,158,11,0.16)', color: '#b45309', borderRadius: 6, padding: '1px 8px', fontWeight: 600 }}><Num>80–100%</Num> כמעט</span>
                <span style={{ background: 'rgba(239,68,68,0.14)', color: '#b91c1c', borderRadius: 6, padding: '1px 8px', fontWeight: 600 }}>מתחת ל-<Num>80%</Num></span>
              </div>
            </Step>

            <Step n={6} icon={<SummarizeOutlined style={{ fontSize: 18 }} />} color="#475569" title='עמודת סה"כ'
              lead={<>כמו בטבלה של שטראוס:</>}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', marginTop: 8, fontSize: 12.5 }}>
                <b>נדרשים</b><span><Num>12</Num> חודשי הצריכה הראשונים בלבד</span>
                <b>מתומחרים</b><span>כל האופק</span>
                <b>חסר / כיסוי</b><span>מחושבים משני הסכומים האלה</span>
              </div>
            </Step>
          </div>

        </div>
      </div>
    </div>
  </div>
);

// ─── Cell explanations (tooltip content + which cells to highlight) ───────────

interface Explain {
  title: string;
  body: React.ReactNode;
  note?: string;
  warn?: string;
  deps: Array<[string, Role]>;
}

const cid = (code: string, key: string) => `${code}|${key}`;
const mid = (code: string, y: number, m: number) => `${code}|m|${y}-${m}`;

interface Ctx {
  data: TerminalCoverageData;
  nameOf: (_code: string) => string;
  /** "רשיו · LSEG יומי" when the material is linked to a live ratio, else "רשיו · קבוע". */
  ratioLabel: (_code: string) => string;
  /** Date of the latest physical-stock report per material (ISO), if any. */
  stockDates: Record<string, string>;
  monthlyByMat: Record<string, Record<string, number>>; // `${y}-${m}` -> code -> MT
}

/** Ids of the monthly cells for the 2 months after a terminal's consumption (the emergency-stock months). */
function bufferMonthDeps(ctx: Ctx, t: TerminalData): Array<[string, Role]> {
  const last = t.months[t.months.length - 1];
  if (!last) return [];
  const out: Array<[string, Role]> = [];
  for (let j = 1; j <= 2; j++) {
    const i = last.year * 12 + last.month - 1 + j;
    const y = Math.floor(i / 12), m = (i % 12) + 1;
    const owner = ctx.data.terminals.find(x => x.months.some(mm => mm.year === y && mm.month === m));
    if (owner) out.push([mid(owner.code, y, m), 'plus']);
  }
  return out;
}

/** a + b + c = total, as one formula row. */
const SumFormula: React.FC<{ parts: Array<{ v: string; label: string }>; total: string; totalLabel: string; role?: Role }> = ({ parts, total, totalLabel, role = 'plus' }) => (
  <Formula>
    {parts.map((p, i) => <React.Fragment key={i}>{i > 0 && <Op>+</Op>}<Term v={p.v} label={p.label} role={role} /></React.Fragment>)}
    <Op>=</Op><Term v={total} label={totalLabel} role="result" />
  </Formula>
);

const List: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: 10, fontSize: 12.5 }}>
    <div style={{ color: '#64748b', fontWeight: 600, marginBottom: 2 }}>{title}</div>
    <div style={{ display: 'grid', gap: 2 }}>{children}</div>
  </div>
);

const daysAgo = (iso: string) => Math.floor((Date.now() - new Date(`${iso.slice(0, 10)}T00:00:00`).getTime()) / 86400000);

/** "דווח 15.07.26" per material + a warning when the oldest report is over 30 days old. */
function stockInfo(ctx: Ctx, codes: string[]): { label: (_code: string) => string; warn?: string } {
  const dates = codes.map(c => ctx.stockDates[c]).filter(Boolean) as string[];
  const oldest = dates.sort()[0];
  const age = oldest ? daysAgo(oldest) : 0;
  return {
    label: (code: string) => (ctx.stockDates[code] ? `דווח ${fmtDate(ctx.stockDates[code])}` : 'אין דיווח'),
    warn: oldest && age > 30
      ? `ספירת המלאי האחרונה היא מ-${fmtDate(oldest)}, לפני ${age} ימים. המלאי בפועל היום כנראה שונה. כדאי לבקש דיווח מלאי עדכני.`
      : undefined,
  };
}

function explainCell(key: RowKey, t: TerminalData, idx: number, ctx: Ctx): Explain {
  const ccy = ccyPrefix(t.avgCurrency);
  const prev = idx > 0 ? ctx.data.terminals[idx - 1] : null;
  const name = (code: string) => ctx.nameOf(code);
  const C = (mt: number) => n1(mt / CONTRACT_SIZE_MT);
  switch (key) {
    case 'demand':
      return {
        title: `צריכה לטרמינל ${t.label}`,
        body: <SumFormula parts={t.months.map(m => ({ v: fmt(m.demandMT), label: m.label }))} total={fmt(t.demandMT)} totalLabel="טון" />,
        note: 'סכום הצריכה החודשית מה-MRP, חמאה ועיסה יחד.',
        deps: t.months.map(m => [mid(t.code, m.year, m.month), 'plus']),
      };
    case 'buffer':
      return {
        title: `מלאי חירום בסוף ${t.label}`,
        body: <SumFormula parts={t.materials.map(m => ({ v: n1(m.bufferMT), label: name(m.formulaCode) }))} total={fmt(t.bufferMT)} totalLabel="טון" />,
        note: 'הצריכה של החודשיים שאחרי הטרמינל: המלאי שצריך להישאר במחסן בסוף התקופה.',
        deps: bufferMonthDeps(ctx, t),
      };
    case 'available':
      return t.isCurrent ? {
        title: `מלאי קיים בתחילת ${t.label}`,
        body: <SumFormula parts={t.materials.map(m => ({ v: n1(m.availableMT), label: `${name(m.formulaCode)} · ${stockInfo(ctx, [m.formulaCode]).label(m.formulaCode)}` }))} total={fmt(t.inventoryMT)} totalLabel="טון" />,
        note: 'הטרמינל הנוכחי: המלאי הפיזי האחרון שדווח, כפי שהוא, בלי קיזוזים.',
        warn: stockInfo(ctx, t.materials.map(m => m.formulaCode)).warn,
        deps: [],
      } : {
        title: `מלאי קיים בתחילת ${t.label}`,
        body: <Formula><Term v={fmt(prev?.bufferMT ?? 0)} label={`מלאי החירום של ${prev?.label ?? ''}`} role="plus" /><Op>=</Op><Term v={fmt(t.inventoryMT)} label="טון" role="result" /></Formula>,
        note: 'מניחים שהטרמינל הקודם גודר במלואו, ולכן בתחילת הטרמינל הזה נשאר בדיוק מלאי החירום שלו.',
        deps: prev ? [[cid(prev.code, 'buffer'), 'plus']] : [],
      };
    case 'exchange':
      return {
        title: `חוזי בורסה · ${t.label}`,
        body: t.hedges.length ? (
          <>
            <SumFormula parts={t.hedges.map(h => ({ v: C(h.quantityMT), label: `${h.isOption ? 'אופציות' : 'חוזים'} · ${fmtDate(h.maturityDate)}` }))} total={C(t.actualMT)} totalLabel="חוזים" />
          </>
        ) : <span>אין חוזי בורסה שפוקעים בטרמינל הזה.</span>,
        note: 'חוזים שהפקיעה שלהם אחרי הטרמינל הקודם ועד חודש החוזה של הטרמינל הזה, לפי הדוח האחרון של הברוקר. אופציה נספרת כחוזה.',
        deps: [],
      };
    case 'pos':
      return {
        title: `הזמנות רכש במחיר קבוע · ${t.label}`,
        body: t.posMT > 0 ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {t.materials.filter(m => m.posMT > 0).map(m => (
              <Formula key={m.formulaCode}>
                <Term v={n1(m.posMT)} label={`טון ${name(m.formulaCode)}`} role="plus" /><Op>×</Op>
                <Term v={m.ratio.toFixed(2)} label={ctx.ratioLabel(m.formulaCode)} role="factor" /><Op>÷</Op>
                <Term v="10" label="טון לחוזה" role="divisor" /><Op>=</Op>
                <Term v={C(m.posBeansMT)} label="חוזים" />
              </Formula>
            ))}
            {t.materials.filter(m => m.posMT > 0).length > 1 && <Formula><Term v={C(t.posBeansMT)} label="סה״כ חוזים" role="result" /></Formula>}
          </div>
        ) : <span>אין הזמנות במחיר קבוע שמגיעות ב{t.windowSpan}.</span>,
        note: 'הזמנות במחיר קבוע שמגיעות בחודשי הצריכה של הטרמינל, מהחודש הנוכחי והלאה. הזמנה שמועד האספקה שלה עבר כבר במלאי. הזמנות רשיו לא נספרות.',
        deps: [],
      };
    case 'required':
      return {
        title: `כמות חוזים נדרשת · ${t.label}`,
        body: (
          <div style={{ display: 'grid', gap: 12 }}>
            {t.materials.map(m => (
              <div key={m.formulaCode}>
                <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 700, marginBottom: 4 }}>{name(m.formulaCode)}</div>
                <Formula>
                  <Term v={n1(m.demandMT)} label="צריכה" role="plus" /><Op>+</Op>
                  <Term v={n1(m.bufferMT)} label="מלאי חירום" role="plus" /><Op>−</Op>
                  <Term v={n1(m.availableMT)} label={t.isCurrent ? `מלאי פיזי · ${stockInfo(ctx, [m.formulaCode]).label(m.formulaCode)}` : 'מלאי חירום קודם'} role="minus" /><Op>=</Op>
                  <Term v={n1(m.netMT)} label="חשיפה (טון)" /><Op>×</Op>
                  <Term v={m.ratio.toFixed(2)} label={ctx.ratioLabel(m.formulaCode)} role="factor" /><Op>=</Op>
                  <Term v={n1(m.requiredBeansMT)} label="טון פולים" />
                </Formula>
                {m.demandMT + m.bufferMT - m.availableMT < 0 && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>המלאי מכסה הכול, ולכן החשיפה היא <Num>0</Num>.</div>}
              </div>
            ))}
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
              <Formula>
                {t.materials.length > 1 && <Op>(</Op>}
                {t.materials.map((m, i) => <React.Fragment key={m.formulaCode}>{i > 0 && <Op>+</Op>}<Term v={n1(m.requiredBeansMT)} label={`פולים · ${name(m.formulaCode)}`} /></React.Fragment>)}
                {t.materials.length > 1 && <Op>)</Op>}
                <Op>÷</Op><Term v="10" label="טון לחוזה" role="divisor" /><Op>=</Op>
                <Term v={C(t.requiredMT)} label="חוזים נדרשים" role="result" />
              </Formula>
            </div>
          </div>
        ),
        warn: t.isCurrent ? stockInfo(ctx, t.materials.map(m => m.formulaCode)).warn : undefined,
        deps: [[cid(t.code, 'demand'), 'plus'], [cid(t.code, 'buffer'), 'plus'], [cid(t.code, 'available'), 'minus']],
      };
    case 'priced':
      return {
        title: `כמות חוזים מתומחרים · ${t.label}`,
        body: <Formula><Term v={C(t.actualMT)} label="חוזי בורסה" role="plus" /><Op>+</Op><Term v={C(t.posBeansMT)} label="הזמנות רכש" role="plus" /><Op>=</Op><Term v={C(t.pricedMT)} label="חוזים מתומחרים" role="result" /></Formula>,
        note: 'הפירוט של כל אחד מהשניים: בשורות "חוזי בורסה" ו"הזמנות רכש" שמעל.',
        deps: [[cid(t.code, 'exchange'), 'plus'], [cid(t.code, 'pos'), 'plus']],
      };
    case 'gap':
      return {
        title: `חסר / עודף · ${t.label}`,
        body: <Formula><Term v={C(t.pricedMT)} label="מתומחרים" role="plus" /><Op>−</Op><Term v={C(t.requiredMT)} label="נדרשים" role="minus" /><Op>=</Op><Term v={signed1(t.gapMT / CONTRACT_SIZE_MT)} label="חוזים" role="result" /></Formula>,
        note: 'פלוס: יש עודף חוזים. מינוס: חסרים חוזים.',
        deps: [[cid(t.code, 'priced'), 'plus'], [cid(t.code, 'required'), 'minus']],
      };
    case 'avg_price':
      return {
        title: `מחיר בורסה ממוצע · ${t.label}`,
        body: t.hedges.length ? (
          <>
            <List title="החוזים בטרמינל:">
              {t.hedges.map((h, i) => (
                <div key={i}>• <Num>{fmt(Math.abs(h.quantityMT))}</Num> טון במחיר <Num>{ccy}{fmt(h.price)}</Num>{h.isOption ? ' (אופציה, מחיר סטרייק)' : ''}</div>
              ))}
            </List>
            <div style={{ marginTop: 10 }}><Formula><Term v={`${ccy}${fmt(t.avgPrice)}`} label="ממוצע משוקלל לפי כמות" role="result" /></Formula></div>
          </>
        ) : <span>אין חוזי בורסה בטרמינל.</span>,
        note: 'מחיר הכניסה של החוזים הפתוחים, לפי הדוח האחרון של הברוקר.',
        deps: [],
      };
    case 'closed_ratio':
      return {
        title: `רשיו סגור · ${t.label}`,
        body: t.ratioPOs.length ? (
          <>
            <List title="הזמנות רשיו בטרמינל:">
              {t.ratioPOs.map((p, i) => (
                <div key={i}>• {name(p.formulaCode)}: <Num>{n1(p.quantity)}</Num> טון ברשיו <Num>{p.ratio.toFixed(2)}</Num> · אספקה <Num>{fmtDate(p.deliveryPeriod)}</Num></div>
              ))}
            </List>
            <div style={{ marginTop: 10 }}><Formula><Term v={t.agreedRatio.toFixed(2)} label="ממוצע משוקלל לפי כמות" role="result" /></Formula></div>
          </>
        ) : <span>אין הזמנות רשיו שמגיעות ב{t.windowSpan}.</span>,
        note: 'בהזמנת רשיו הכמות סגורה מול הספק, והמחיר ייקבע לפי הבורסה. לכן היא לא נספרת כמתומחרת.',
        deps: [],
      };
    case 'coverage':
      return {
        title: `אחוז כיסוי · ${t.label}`,
        body: <Formula><Term v={C(t.pricedMT)} label="מתומחרים" role="plus" /><Op>÷</Op><Term v={C(t.requiredMT)} label="נדרשים" role="divisor" /><Op>=</Op><Term v={formatCell('coverage', t.coveragePct)} label="כיסוי" role="result" /></Formula>,
        note: t.requiredMT > 0 ? undefined : 'אין צורך בחוזים (נדרשים 0), ולכן הכיסוי בלתי מוגבל (∞).',
        deps: [[cid(t.code, 'priced'), 'plus'], [cid(t.code, 'required'), 'divisor']],
      };
    default:
      return { title: '', body: null, deps: [] };
  }
}

function explainMonth(t: TerminalData, m: TerminalData['months'][number], ctx: Ctx): Explain {
  const byMat = ctx.monthlyByMat[`${m.year}-${m.month}`] || {};
  const parts = Object.entries(byMat);
  return {
    title: `צריכה · ${HEB_MONTHS[m.month - 1]} ${m.year}`,
    body: parts.length
      ? <SumFormula parts={parts.map(([code, q]) => ({ v: n1(q), label: ctx.nameOf(code) }))} total={fmt(m.demandMT)} totalLabel="טון" />
      : <span>אין צריכה בחודש הזה ב-MRP.</span>,
    note: `מתוך ה-MRP (תחזית הצריכה). שייך לטרמינל ${t.label}.`,
    deps: [],
  };
}

function explainTotal(key: RowKey, ctx: Ctx): Explain | null {
  const { terminals, totalData } = ctx.data;
  const C = (v: number) => n1(v / CONTRACT_SIZE_MT);
  switch (key) {
    case 'required': {
      const inc = terminals.filter(t => t.inTotal12 > 0);
      return {
        title: 'סה"כ נדרשים · 12 חודשי צריכה',
        body: <SumFormula parts={inc.map(t => ({ v: C(t.requiredMT * t.inTotal12), label: t.inTotal12 < 1 ? `${t.code} (${Math.round(t.inTotal12 * 100)}%)` : t.code }))} total={C(totalData.requiredMT12)} totalLabel="חוזים" />,
        note: 'רק 12 חודשי הצריכה הראשונים, כמו בטבלה של שטראוס. טרמינל שחוצה את החודש ה-12 נכנס באופן יחסי לצריכה שלו.',
        deps: inc.map(t => [cid(t.code, 'required'), 'plus']),
      };
    }
    case 'exchange':
    case 'pos': {
      const val = (t: TerminalData) => (key === 'exchange' ? t.actualMT : t.posBeansMT);
      return {
        title: `סה"כ ${key === 'exchange' ? 'חוזי בורסה' : 'הזמנות רכש'} · כל האופק`,
        body: <SumFormula parts={terminals.filter(t => val(t)).map(t => ({ v: C(val(t)), label: t.code }))} total={C(key === 'exchange' ? totalData.actualMT : totalData.posBeansMT)} totalLabel="חוזים" />,
        deps: terminals.map(t => [cid(t.code, key), 'plus']),
      };
    }
    case 'priced':
      return {
        title: 'סה"כ מתומחרים · כל האופק',
        body: <Formula><Term v={C(totalData.actualMT)} label="חוזי בורסה" role="plus" /><Op>+</Op><Term v={C(totalData.posBeansMT)} label="הזמנות רכש" role="plus" /><Op>=</Op><Term v={C(totalData.pricedMT)} label="חוזים" role="result" /></Formula>,
        deps: [[cid('total', 'exchange'), 'plus'], [cid('total', 'pos'), 'plus']],
      };
    case 'gap':
      return {
        title: 'סה"כ חסר / עודף',
        body: <Formula><Term v={C(totalData.pricedMT)} label="מתומחרים" role="plus" /><Op>−</Op><Term v={C(totalData.requiredMT12)} label="נדרשים (12 ח׳)" role="minus" /><Op>=</Op><Term v={signed1(totalData.gapMT / CONTRACT_SIZE_MT)} label="חוזים" role="result" /></Formula>,
        deps: [[cid('total', 'priced'), 'plus'], [cid('total', 'required'), 'minus']],
      };
    case 'coverage':
      return {
        title: 'סה"כ אחוז כיסוי',
        body: <Formula><Term v={C(totalData.pricedMT)} label="מתומחרים" role="plus" /><Op>÷</Op><Term v={C(totalData.requiredMT12)} label="נדרשים (12 ח׳)" role="divisor" /><Op>=</Op><Term v={formatCell('coverage', totalData.coveragePct)} label="כיסוי" role="result" /></Formula>,
        deps: [[cid('total', 'priced'), 'plus'], [cid('total', 'required'), 'divisor']],
      };
    case 'demand':
    case 'monthly_demand':
      return {
        title: 'סה"כ צריכה',
        body: <Formula><Term v={fmt(totalData.demandMT)} label="טון, כל חודשי הצריכה באופק" role="result" /></Formula>,
        deps: terminals.map(t => [cid(t.code, 'demand'), 'plus']),
      };
    case 'avg_price':
      return { title: 'מחיר בורסה ממוצע · כל החוזים', body: <Formula><Term v={`${ccyPrefix(totalData.avgCurrency)}${fmt(totalData.avgPrice)}`} label="ממוצע משוקלל של כל החוזים הפתוחים" role="result" /></Formula>, note: 'אופציות לפי מחיר הסטרייק.', deps: [] };
    case 'closed_ratio':
      return { title: 'רשיו סגור · כל ההזמנות', body: <Formula><Term v={totalData.agreedRatio > 0 ? totalData.agreedRatio.toFixed(2) : '—'} label="ממוצע משוקלל של כל הזמנות הרשיו" role="result" /></Formula>, deps: [] };
    default:
      return null;
  }
}

const TipCard: React.FC<{ ex: Explain }> = ({ ex }) => (
  <div dir="rtl" style={{ textAlign: 'right', padding: '12px 14px', minWidth: 260, fontFamily: FONT }}>
    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 10 }}>{ex.title}</div>
    <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }}>{ex.body}</div>
    {ex.note && <div style={{ marginTop: 10, fontSize: 12, color: '#64748b', lineHeight: 1.6, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>{ex.note}</div>}
    {ex.warn && <div style={{ marginTop: 8, fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 8px', lineHeight: 1.5 }}>⚠ {ex.warn}</div>}
  </div>
);

// Light, slightly translucent card. Cell tooltips open BELOW the cell (never flipping above),
// because a value's inputs are in the rows above it or the same row — so the card never hides
// the cells it highlights. The popper ignores the mouse, so moving on to the next cell is smooth.
const tipSx = {
  tooltip: { sx: { fontFamily: FONT, bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(6px)', color: '#0f172a', border: '1px solid #e2e8f0', boxShadow: '0 12px 32px rgba(15,23,42,0.14)', borderRadius: '12px', maxWidth: 560, p: 0 } },
  arrow: { sx: { color: 'rgba(255,255,255,0.94)', '&::before': { border: '1px solid #e2e8f0' } } },
  popper: { sx: { pointerEvents: 'none' } },
};
const belowPopper = {
  modifiers: [
    { name: 'flip', options: { fallbackPlacements: ['bottom-start', 'bottom-end'] } },
    { name: 'offset', options: { offset: [0, 4] } },
  ],
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const TerminalCoverageTable: React.FC<Props> = ({ accountId, baseMaterialId }) => {
  const { data: baseHedgeMaterials } = useBaseHedgeMaterials(accountId);
  const { data: formulasPricesData } = useFormulasPrices();

  const [horizonMonths, setHorizonMonths] = useState<number>(() => {
    const saved = Number(localStorage.getItem(`terminal-coverage-horizon-${accountId}`));
    return saved > 0 ? saved : DEFAULT_HORIZON_MONTHS;
  });
  const changeHorizon = useCallback((n: number) => {
    setHorizonMonths(n);
    try { localStorage.setItem(`terminal-coverage-horizon-${accountId}`, String(n)); } catch { /* ignore */ }
  }, [accountId]);

  const baseTemplate = baseHedgeMaterials?.[0];
  const templateId = baseTemplate?.id ? parseInt(baseTemplate.id, 10) : undefined;
  const { data: derivedMaterialsRaw } = useDerivedMaterials(templateId);
  const formulaCode = baseTemplate?.formula_code ?? baseMaterialId;

  const pricesMap = useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    const map: Record<string, number> = {};
    Object.entries(formulasPricesData.pricesMap).forEach(([code, info]) => { map[code] = info.price; });
    return map;
  }, [formulasPricesData]);

  // Terminal windows + per-material demand / emergency stock / physical stock / ratio from the
  // backend derivation engine, with the account's consumption lead (Strauss: 2 months).
  const { data: derivationResult } = useQuery(
    ['terminal-coverage-derivation', accountId, pricesMap, horizonMonths],
    () => getDerivedHedges(accountId, pricesMap, horizonMonths, true),
    { enabled: !!accountId && Object.keys(pricesMap).length > 0, staleTime: 30000, refetchOnWindowFocus: false, keepPreviousData: true },
  );
  const allocations = derivationResult?.allocations ?? [];
  const consumptionLeadMonths = derivationResult?.consumptionLeadMonths ?? 0;

  const { data: baseHedges = [] } = useQuery<HedgePosition[]>(
    ['base-hedges-terminal', accountId],
    () => getHedgePositions({ accountId }),
    { enabled: !!accountId, staleTime: 30000, refetchOnWindowFocus: false },
  );
  const hedgePositions = useMemo(() => baseHedges.filter(h => h.formula_code === formulaCode), [baseHedges, formulaCode]);

  const materialCodes = useMemo(() => {
    const s = new Set<string>();
    allocations.forEach(a => Object.keys(a.materials).forEach(c => s.add(c)));
    return s;
  }, [allocations]);

  const { data: demandPositions = [] } = useQuery(
    ['terminal-coverage-demand', accountId],
    () => getDemandPositions({ accountId }),
    { enabled: !!accountId, staleTime: 30000, refetchOnWindowFocus: false },
  );

  const { monthlyDemand, monthlyByMat, nameByCode } = useMemo(() => {
    const map: Record<string, number> = {};
    const byMat: Record<string, Record<string, number>> = {};
    const names: Record<string, string> = {};
    demandPositions.forEach(p => {
      if (!materialCodes.has(p.formula_code)) return;
      const [y, m] = String(p.delivery_period).slice(0, 7).split('-').map(Number);
      // Full month (original_quantity), not the current month pro-rated to the remaining days.
      const k = `${y}-${m}`, q = Math.abs(Number(p.original_quantity ?? p.quantity) || 0);
      map[k] = (map[k] || 0) + q;
      byMat[k] = byMat[k] || {};
      byMat[k][p.formula_code] = (byMat[k][p.formula_code] || 0) + q;
      if (p.formula_name) names[p.formula_code] = p.formula_name;
    });
    return { monthlyDemand: map, monthlyByMat: byMat, nameByCode: names };
  }, [demandPositions, materialCodes]);
  const nameOf = useCallback((code: string) => heName(nameByCode[code], code), [nameByCode]);
  // Where each material's ratio comes from: a live LSEG ratio (linked) or the fixed template value.
  // Live ratios use the latest stored price of the linked instrument (shown with its date); the
  // stored value is used only if that instrument has never had a price.
  const ratioLabel = useCallback((code: string) => {
    const dm = (derivedMaterialsRaw ?? []).find((d: any) => d.formula_code === code);
    if (!dm?.linked_ratio_code) return 'רשיו · קבוע';
    const live = formulasPricesData?.pricesMap?.[dm.linked_ratio_code];
    if (!live?.price) return 'רשיו · קבוע (אין עדיין רשיו יומי)';
    return live.priceDate ? `רשיו · LSEG ${fmtDate(String(live.priceDate))}` : 'רשיו · LSEG יומי';
  }, [derivedMaterialsRaw, formulasPricesData]);

  const { data: supplyPositions = [] } = useQuery(
    ['terminal-coverage-supply', accountId],
    () => getSupplyPositions({ accountId }),
    { enabled: !!accountId, staleTime: 30000, refetchOnWindowFocus: false },
  );

  // Ratio POs → "רשיו סגור"; fixed-price POs → priced contracts. The supply API moves past-due
  // positions into the current month — use the real delivery date (original_due_date) instead.
  const stockDates = useMemo(() => {
    const out: Record<string, string> = {};
    supplyPositions
      .filter(p => p.supply_type === 'on_hand_inventory' && materialCodes.has(p.formula_code))
      .forEach(p => {
        const d = String(p.original_due_date || p.delivery_or_available_period || '').slice(0, 10);
        if (d && (!out[p.formula_code] || d > out[p.formula_code])) out[p.formula_code] = d;
      });
    return out;
  }, [supplyPositions, materialCodes]);

  const { ratioPOs, fixedPOs } = useMemo(() => {
    const mine = supplyPositions.filter(p => materialCodes.has(p.formula_code));
    const realDate = (p: typeof mine[number]) => p.original_due_date || p.delivery_or_available_period;
    return {
      ratioPOs: mine
        .filter(p => p.supply_type === 'ratio_po')
        .map(p => ({ formulaCode: p.formula_code, deliveryPeriod: realDate(p), quantity: Math.abs(Number(p.quantity) || 0), ratio: Number(p.notes?.match(/ratio=([\d.]+)/)?.[1]) || 0 }))
        .filter(p => p.ratio > 0),
      fixedPOs: mine
        .filter(p => p.supply_type === 'purchase_order')
        .map(p => ({ formulaCode: p.formula_code, deliveryPeriod: realDate(p), quantity: Math.abs(Number(p.quantity) || 0) })),
    };
  }, [supplyPositions, materialCodes]);

  const baseCurrency = useBaseCurrency();
  const { convertToBase } = useFx(baseCurrency);
  const fxConvert = useCallback(
    (amount: number, currency: string) => convertToBase(amount, currency).amountBase ?? amount,
    [convertToBase],
  );

  const data = useMemo(() => {
    if (!formulaCode || !allocations.length) return null;
    return calculateTerminalCoverage({
      formulaCode, allocations, hedgePositions, monthlyDemand, baseCurrency, fxConvert, ratioPOs, fixedPOs, consumptionLeadMonths,
    });
  }, [formulaCode, allocations, hedgePositions, monthlyDemand, baseCurrency, fxConvert, ratioPOs, fixedPOs, consumptionLeadMonths]);

  // Full view (every calculation step, top to bottom) vs compact (just the Strauss rows).
  const [showDetail, setShowDetail] = useState(() => localStorage.getItem('terminal-coverage-compact') !== '1');
  const changeDetail = (v: boolean) => {
    setShowDetail(v);
    try { localStorage.setItem('terminal-coverage-compact', v ? '' : '1'); } catch { /* ignore */ }
  };
  const [showHow, setShowHow] = useState(() => localStorage.getItem('terminal-coverage-how-hidden') !== '1');
  const toggleHow = (v: boolean) => {
    setShowHow(v);
    try { localStorage.setItem('terminal-coverage-how-hidden', v ? '' : '1'); } catch { /* ignore */ }
  };

  // Hovered cell + the cells its value is calculated from.
  const [hover, setHover] = useState<{ id: string; deps: Map<string, Role> } | null>(null);

  if (!data || !data.terminals.length) {
    return (
      <div className="flex flex-col h-full p-8 items-center justify-center" style={{ color: '#9ca3af' }}>
        <p className="text-sm font-medium">לא הוגדרו טרמינלים</p>
        <p className="text-xs mt-1">נדרש תבנית גזירת חוזים עם לוח מסחר טרמינלים.</p>
      </div>
    );
  }

  const ctx: Ctx = { data, nameOf, ratioLabel, stockDates, monthlyByMat };
  const { terminals, totalData, allMonths } = data;
  const visibleRows = ROW_DEFS.filter(r => showDetail || !r.detail);
  const colCount = 2 + terminals.reduce((s, t) => s + t.months.length, 0);

  const totalValue = (key: RowKey): number | null => {
    switch (key) {
      case 'monthly_demand': return allMonths.reduce((s, m) => s + m.demandMT, 0);
      case 'demand':         return totalData.demandMT;
      case 'required':       return totalData.requiredMT12 / CONTRACT_SIZE_MT;
      case 'exchange':       return totalData.actualMT / CONTRACT_SIZE_MT;
      case 'pos':            return totalData.posBeansMT / CONTRACT_SIZE_MT;
      case 'priced':         return totalData.pricedMT / CONTRACT_SIZE_MT;
      case 'gap':            return totalData.gapMT / CONTRACT_SIZE_MT;
      case 'avg_price':      return totalData.avgPrice;
      case 'closed_ratio':   return totalData.agreedRatio;
      case 'coverage':       return totalData.coveragePct;
      default:               return null; // emergency stock / stock / POs: per terminal only
    }
  };

  const hoverBg = (id: string): string | undefined => {
    if (!hover) return undefined;
    if (hover.id === id) return ROLE.result.tint;
    const r = hover.deps.get(id);
    return r ? ROLE[r].tint : undefined;
  };
  const hoverProps = (id: string, ex: Explain | null) => ({
    onMouseEnter: () => ex && setHover({ id, deps: new Map(ex.deps) }),
    onMouseLeave: () => setHover(h => (h?.id === id ? null : h)),
  });

  // A table cell with a rich explanation tooltip + dependency highlighting (plain render
  // function, not a component, so hover state changes don't remount the tooltip).
  const cell = (id: string, ex: Explain | null, style: React.CSSProperties, children: React.ReactNode, colSpan?: number, className?: string) => {
    const bg = hoverBg(id);
    const td = (
      <td key={id} colSpan={colSpan} className={className} {...hoverProps(id, ex)}
        style={{ ...style, ...(bg ? { backgroundColor: bg, boxShadow: hover?.id === id ? 'inset 0 0 0 2px #f59e0b' : undefined } : {}), cursor: ex ? 'help' : undefined, transition: 'background-color .12s' }}>
        {children}
      </td>
    );
    return ex
      ? <Tooltip key={id} title={<TipCard ex={ex} />} placement="bottom" arrow disableInteractive enterDelay={250} componentsProps={tipSx} PopperProps={belowPopper}>{td}</Tooltip>
      : td;
  };

  return (
    <div className="flex flex-col h-full overflow-auto terminal-coverage-root" dir="rtl" style={{ fontFamily: "'Open Sans', 'Heebo', 'Segoe UI', system-ui, sans-serif" }}>
      <style>{`.terminal-coverage-root .tabular-nums { direction: ltr; unicode-bidi: embed; }`}</style>
      <div className="px-4 pt-3 pb-2 flex items-start justify-between" style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div>
          <h2 className="text-base font-semibold" style={{ color: '#111827' }}>
            {baseTemplate?.formula_name ?? baseMaterialId} — כיסוי טרמינלים
          </h2>
          <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
            חוזים נדרשים מול חוזים מתומחרים, לפי טרמינל
            {consumptionLeadMonths > 0 && ` · כל טרמינל מכסה את הצריכה של ${consumptionLeadMonths} החודשים שאחריו`}
          </p>
        </div>
      </div>

      <HowItWorks lead={consumptionLeadMonths} open={showHow} onToggle={() => toggleHow(!showHow)} />

      {/* Table toolbar: view + horizon, right above the table */}
      <div className="mx-4 mt-5 mb-3 flex items-center justify-between flex-wrap gap-3" style={{ fontFamily: FONT }}>
        <div className="flex items-center gap-3 flex-wrap">
          <div style={{ display: 'flex', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, padding: 3, fontSize: 12.5 }}>
            {([[true, 'תצוגה מלאה'], [false, 'תצוגה מקוצרת']] as const).map(([v, label]) => (
              <button key={label} onClick={() => changeDetail(v)}
                style={{
                  border: 'none', cursor: 'pointer', borderRadius: 7, padding: '4px 12px', fontFamily: FONT,
                  background: showDetail === v ? '#fff' : 'transparent', color: showDetail === v ? '#0f172a' : '#64748b',
                  fontWeight: showDetail === v ? 700 : 500, boxShadow: showDetail === v ? '0 1px 3px rgba(15,23,42,0.12)' : 'none',
                }}>{label}</button>
            ))}
          </div>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {showDetail
              ? 'כל שלבי החישוב, שורה אחרי שורה: צריכה, מלאי חירום, מלאי קיים, חוזי בורסה והזמנות.'
              : 'רק השורות של טבלת שטראוס, בלי שלבי הביניים.'}
          </span>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap' }}>
          <span>אופק תצוגה</span>
          <select
            value={horizonMonths}
            onChange={e => changeHorizon(Number(e.target.value))}
            style={{
              fontSize: 12.5, padding: '4px 10px 4px 28px', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a', cursor: 'pointer', fontFamily: FONT,
              appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundColor: '#fff',
              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='3'><path d='M6 9l6 6 6-6'/></svg>")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'left 10px center', backgroundSize: '10px',
            }}
          >
            {HORIZON_OPTIONS.map(n => <option key={n} value={n}>{n} חודשים</option>)}
          </select>
        </label>
      </div>

      {/* Hover hint + colour legend, right above the table (always visible) */}
      <div className="mx-4 mb-3 flex items-center flex-wrap gap-2" style={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>
        <TouchAppOutlined style={{ fontSize: 15, color: '#2563eb' }} />
        <span><b style={{ color: '#334155' }}>רחפו על כל תא</b> כדי לראות מאיפה הוא מגיע. התאים שמשתתפים בחישוב נצבעים:</span>
        <Badge role="plus">מתווסף</Badge><Badge role="minus">מופחת</Badge><Badge role="divisor">מחלק</Badge><Badge role="result">התוצאה</Badge>
      </div>

      {/* Only horizontal scrolling here — the whole page (explanation + table) scrolls vertically together. */}
      <div className="mx-4" style={{ flexShrink: 0, marginBottom: 72 }}>
        {/* Light "ledger" frame: thin border, soft paper background, subtle inner rule */}
        <div className="overflow-x-auto" style={{ border: '1px solid #d6dbe3', borderRadius: 12, background: '#fff', boxShadow: '0 1px 0 #eef1f5, 0 6px 18px rgba(15,23,42,0.04)', padding: '6px 6px 10px' }}>
        <table className="text-xs border-separate border-spacing-0 min-w-full">
          <thead>
            <tr>
              <th className="sticky right-0 z-20 bg-white min-w-[200px]" style={{ borderBottom: '2px solid #e5e7eb', borderLeft: '2px solid #e5e7eb' }} />
              {terminals.map(t => (
                <th key={`hdr-${t.code}`} colSpan={t.months.length} className="text-center px-2 py-2.5 whitespace-nowrap"
                  style={{ borderBottom: '2px solid #d1d5db', borderRight: '2px solid #e5e7eb', backgroundColor: t.isCurrent ? '#fef9c3' : '#f9fafb' }}>
                  <Tooltip title={<TipCard ex={{ title: `חוזה ${t.label} (${t.code})`, body: <Formula><Term v={t.label} label="חודש החוזה" /><Op>→</Op><Term v={t.windowSpan} label="חודשי הצריכה" role="plus" /></Formula>, note: t.isCurrent ? 'הטרמינל הנוכחי: הצריכה שלו כוללת את החודש הזה. החישוב שלו משתמש במלאי הפיזי.' : undefined, deps: [] }} />} placement="top" arrow componentsProps={tipSx}>
                    <div style={{ cursor: 'help' }}>
                      <div className="font-bold" style={{ color: '#374151' }}>{t.label}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af' }}>{t.code}{t.isCurrent ? ' · נוכחי' : ''}</div>
                    </div>
                  </Tooltip>
                </th>
              ))}
              <th rowSpan={2} className="sticky left-0 z-20 text-center px-3 py-2.5 whitespace-nowrap"
                style={{ borderBottom: '2px solid #d1d5db', borderRight: '2px solid #9ca3af', backgroundColor: '#eff6ff', color: '#1f2937', fontWeight: 700 }}>
                {'סה"כ'}
              </th>
            </tr>
            <tr>
              <th className="sticky right-0 z-20 bg-white text-right px-3 py-2.5 font-medium" style={{ borderBottom: '1px solid #e5e7eb', borderLeft: '2px solid #e5e7eb', color: '#6b7280' }}>
                חודש צריכה
              </th>
              {terminals.map(t => t.months.map((m, mi) => (
                <th key={`${m.year}-${m.month}`} className="text-center px-2 py-2.5 whitespace-nowrap"
                  style={{ borderBottom: '1px solid #e5e7eb', borderRight: mi === 0 ? '2px solid #e5e7eb' : '1px solid #f3f4f6', fontWeight: 400, color: '#6b7280' }}>
                  {m.label} <span style={{ color: '#cbd5e1' }}>{String(m.year).slice(2)}</span>
                </th>
              )))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, ri) => {
              const sec = SECTIONS[row.section];
              const isTotalRow = row.sign === '=';
              const rowBg = isTotalRow ? '#f8fafc' : '#ffffff';
              const sectionStart = ri === 0 || visibleRows[ri - 1].section !== row.section;
              const rowBorder = isTotalRow ? '1px solid #e2e8f0' : '1px solid #f3f4f6';
              return (
                <React.Fragment key={row.key}>
                {sectionStart && (
                  <tr>
                    <td colSpan={colCount} style={{ padding: '18px 0 6px', borderBottom: `2px solid ${sec.color}33` }}>
                      <span className="sticky right-0 flex items-center gap-2 px-3" style={{ width: 'fit-content' }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: sec.color }} />
                        <span style={{ fontWeight: 700, fontSize: 12, color: sec.color }}>{sec.label}</span>
                        {sec.hint && <span style={{ fontSize: 11, color: '#94a3b8' }}>{sec.hint}</span>}
                      </span>
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="sticky right-0 z-10 px-3 py-2.5 whitespace-nowrap" style={{ backgroundColor: rowBg, borderBottom: rowBorder, borderLeft: '2px solid #e5e7eb' }}>
                    <div className="flex items-center gap-2">
                      <span style={{
                        width: 18, height: 18, flexShrink: 0, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 800, lineHeight: 1,
                        background: row.sign === '+' ? ROLE.plus.bg : row.sign === '−' ? ROLE.minus.bg : row.sign === '=' ? ROLE.result.bg : 'transparent',
                        color: row.sign === '+' ? ROLE.plus.fg : row.sign === '−' ? ROLE.minus.fg : ROLE.result.fg,
                      }}>{row.sign}</span>
                      <div>
                        <div className="font-semibold" style={{ color: row.detail ? '#475569' : '#1f2937' }}>{row.label}</div>
                        <div className="font-normal" style={{ fontSize: '10px', color: '#9ca3af' }}>{row.subtitle}</div>
                      </div>
                    </div>
                  </td>

                  {terminals.map((t, tIdx) => {
                    if (row.key === 'monthly_demand') {
                      return t.months.map((m, mi) => {
                        return cell(
                          mid(t.code, m.year, m.month), explainMonth(t, m, ctx),
                          { backgroundColor: rowBg, borderBottom: rowBorder, borderRight: mi === 0 ? '2px solid #e5e7eb' : '1px solid #f3f4f6', color: '#374151' },
                          m.demandMT > 0 ? fmt(m.demandMT) : '—',
                          undefined, 'text-center tabular-nums px-2 py-2.5',
                        );
                      });
                    }
                    const v = cellValue(row.key, t);
                    return cell(
                      cid(t.code, row.key), explainCell(row.key, t, tIdx, ctx),
                      { backgroundColor: cellBg(row.key, v) ?? rowBg, borderBottom: rowBorder, borderRight: '2px solid #e5e7eb' },
                      <span className={isBold(row.key) ? 'font-bold' : ''} style={{ color: cellColor(row.key, v) }}>{formatCell(row.key, v, t.avgCurrency)}</span>,
                      t.months.length, 'text-center tabular-nums px-2 py-2.5',
                    );
                  })}

                  {(() => {
                    const v = totalValue(row.key);
                    return cell(
                      cid('total', row.key), v === null ? null : explainTotal(row.key, ctx),
                      { backgroundColor: cellBg(row.key, v) ?? '#eaf2fe', borderBottom: rowBorder, borderRight: '2px solid #9ca3af' },
                      <span className={isBold(row.key) ? 'font-bold' : ''} style={{ color: cellColor(row.key, v) }}>{v === null ? '' : formatCell(row.key, v, totalData.avgCurrency)}</span>,
                      undefined, 'sticky left-0 z-10 text-center tabular-nums px-3 py-2.5',
                    );
                  })()}
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};
